import React, { memo, useState } from 'react'
import NumericFormat from 'transfix-ui/components/NumericFormat'
import { IconButton } from 'transfix-ui/core'
import uuid from 'uuid'
import { ClearIcon } from 'transfix-icons'
import 'scss/5_components/_text-input.scss'

export interface ICurrencyInputProps {
  id?: string
  label?: string
  /**
   * Optionally pass in another input placeholder.
   * @default '$'
   */
  placeholder?: string
  value?: number
  onChange: (e: { value: string; id: string }) => void
  onClear?: () => void
  onBlur?: () => void
  invalid?: boolean
  validationMessage?: string
  large?: boolean
  disabled?: boolean
  required?: boolean
  hideRequiredLabel?: boolean
  clearable?: boolean
  allowNegative?: boolean
  dataTest?: string
  ariaLabel?: string
  className?: string
  decimalScale?: number
  fixedDecimalScale?: boolean
  /**
   * Optionally pass in a `-$` in case of credits/other negative values.
   * @default '$'
   */
  prefix?: string
  /**
   * Will set the focus on the input when the component is mounted.
   * @default false
   */
  autoFocus?: boolean
}

/**
 * Re-usable utility function for converting a string value, input through the
 * underlying text input, and returns a number value that can be set in the
 * form's state
 *
 * @example parseFormInput('8,500') // 8500
 * @param {string} fieldValue - Currency amount, i.e. '8,500'
 */
export const parseFormInput = (fieldValue?: string): number | undefined => {
  return fieldValue ? Number.parseFloat(fieldValue.replace(/[^0-9.-]+/g, '')) : undefined
}

export const CurrencyInput = memo<ICurrencyInputProps>(
  ({
    value,
    id = uuid(),
    onChange,
    onBlur,
    onClear = () => onChange({ value: '', id }),
    disabled,
    label,
    placeholder = '$',
    required,
    hideRequiredLabel = false,
    invalid,
    validationMessage,
    large,
    clearable,
    dataTest = 'currency-input',
    ariaLabel,
    className,
    decimalScale,
    fixedDecimalScale = false,
    allowNegative = false,
    prefix = '$',
    autoFocus = false,
  }) => {
    const [hasValue, setHasValue] = useState(!!value)

    const onChangeHandler = ({
      formattedValue,
      value: eventValue,
    }: {
      value: string
      formattedValue: string
    }) => {
      setHasValue(!!formattedValue)
      onChange({ value: eventValue, id })
    }

    const classes = [
      'text-input',
      large && 'text-input-large',
      invalid && 'invalid',
      disabled && 'disabled',
      className,
    ]
      .filter(Boolean)
      .join(' ')

    const validationText = validationMessage || (required && !hideRequiredLabel ? 'Required' : '')

    return (
      <div className={classes}>
        <label
          htmlFor={id}
          className={`label ${disabled ? 'disabled' : ''} ${required ? 'required' : ''}`}>
          {label}
        </label>
        <div className='text-input-container'>
          <NumericFormat
            className={invalid ? 'invalid' : ''}
            type='text'
            prefix={prefix}
            displayType='input'
            value={value ?? ''}
            id={id}
            onValueChange={onChangeHandler}
            onBlur={onBlur}
            disabled={disabled}
            placeholder={placeholder}
            thousandSeparator
            valueIsNumericString
            allowNegative={allowNegative}
            data-test={dataTest}
            aria-label={ariaLabel}
            decimalScale={decimalScale}
            fixedDecimalScale={fixedDecimalScale}
            autoFocus={autoFocus}
          />
          {clearable && hasValue && (
            <IconButton size='medium' onClick={onClear} data-test='clear-button'>
              <ClearIcon size={large ? 'xlarge' : 'large'} />
            </IconButton>
          )}
        </div>
        {validationText && <span className='validation-message'>{validationText}</span>}
      </div>
    )
  }
)

export default CurrencyInput
